import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "manager" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setUsers(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/register", form);
      toast.success(`Invited ${form.email}`);
      setForm({ email: "", password: "", full_name: "", role: "manager" });
      setShowAdd(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create user");
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u, newRole) => {
    if (u.role === newRole) return;
    try {
      await api.patch(`/users/${u.id}/role`, { role: newRole });
      toast.success(`${u.email} → ${newRole.toUpperCase()}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`Deleted ${u.email}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-8 max-w-[1200px] mx-auto" data-testid="users-admin-page">
      <div className="flex items-end justify-between border-b border-rule pb-6 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Admin · Team access</div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Users.</h1>
          <p className="text-quiet text-sm mt-3 max-w-xl">
            Only HR admins can add, remove, or re-assign roles. Self-sign-up is disabled — every
            account is invited from here.
          </p>
        </div>
        <button
          data-testid="add-user-btn"
          onClick={() => setShowAdd(true)}
          className="bg-klein hover:bg-klein-hover text-white px-5 py-3 text-sm font-medium"
        >
          + Invite user
        </button>
      </div>

      <div className="mt-8 bg-white border border-rule">
        {loading && <div className="px-5 py-12 text-center text-quiet text-sm">Loading…</div>}
        {!loading && users.length === 0 && (
          <div className="px-5 py-16 text-center text-quiet">No users yet.</div>
        )}
        {!loading && users.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                <Th>User</Th>
                <Th>Role</Th>
                <Th>SMTP</Th>
                <Th>Joined</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} className="border-b border-rule hover:bg-paper" data-testid={`user-row-${u.id}`}>
                    <Td>
                      <div className="text-ink">{u.full_name} {isMe && <span className="text-quiet font-mono text-xs">(you)</span>}</div>
                      <div className="text-xs text-quiet font-mono">{u.email}</div>
                    </Td>
                    <Td>
                      <div className="flex gap-1">
                        {["hr", "manager"].map((r) => (
                          <button
                            key={r}
                            data-testid={`role-${u.id}-${r}`}
                            disabled={isMe && u.role === "hr"}
                            onClick={() => changeRole(u, r)}
                            className={`px-2 py-1 border text-[10px] font-mono uppercase tracking-wider ${
                              u.role === r
                                ? "bg-ink text-white border-ink"
                                : "bg-white text-quiet border-rule hover:border-ink"
                            } ${isMe && u.role === "hr" ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {r === "hr" ? "HR" : "Manager"}
                          </button>
                        ))}
                      </div>
                    </Td>
                    <Td>
                      <span className={`font-mono text-[10px] uppercase tracking-wider ${u.has_smtp ? "text-[#10B981]" : "text-quiet"}`}>
                        {u.has_smtp ? "Configured" : "Not set"}
                      </span>
                    </Td>
                    <Td className="font-mono text-xs text-quiet">
                      {new Date(u.created_at).toLocaleDateString()}
                    </Td>
                    <Td>
                      {!isMe && me?.is_super_admin ? (
                        <button
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => remove(u)}
                          className="text-xs font-mono uppercase tracking-wider text-[#EF4444] hover:bg-[#FEF2F2] border border-[#EF4444] px-2 py-1"
                        >
                          Delete
                        </button>
                      ) : (
                        <span className="text-xs text-quiet font-mono">—</span>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <form
            onSubmit={submit}
            className="bg-white border border-ink max-w-md w-full p-6 fade-in"
            data-testid="add-user-modal"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Admin · New user</div>
                <h3 className="font-serif text-3xl text-ink mt-1 leading-tight">Invite a user.</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="text-quiet hover:text-ink text-xl leading-none"
                data-testid="close-add-user"
              >
                ×
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <Field label="Full name">
                <input data-testid="new-user-name" required value={form.full_name} onChange={change("full_name")} className={inp} />
              </Field>
              <Field label="Work email">
                <input data-testid="new-user-email" required type="email" value={form.email} onChange={change("email")} className={inp} />
              </Field>
              <Field label="Temporary password (min 6)">
                <input data-testid="new-user-password" required type="text" minLength={6} value={form.password} onChange={change("password")} className={inp} />
              </Field>
              <Field label="Role">
                <div className="grid grid-cols-2 gap-2">
                  {["hr", "manager"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`new-user-role-${r}`}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`px-3 py-2.5 border font-mono text-xs uppercase tracking-wider ${
                        form.role === r
                          ? "bg-ink text-white border-ink"
                          : "bg-white text-quiet border-rule hover:border-ink"
                      }`}
                    >
                      {r === "hr" ? "HR Admin" : "Hiring Manager"}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div className="mt-6 flex justify-end gap-2 pt-4 border-t border-rule">
              <button
                type="button"
                onClick={() => setShowAdd(false)}
                className="border border-ink px-4 py-2 text-sm hover:bg-paper"
              >
                Cancel
              </button>
              <button
                data-testid="submit-new-user"
                type="submit"
                disabled={busy}
                className="bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {busy ? "Creating…" : "Create user →"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const inp =
  "w-full bg-transparent border border-rule px-3 py-2.5 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Th = ({ children }) => (
  <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-quiet">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-4 align-top ${className}`}>{children}</td>;
